import { token } from '@atlaskit/tokens';
import { CREATE_PAGE_TEMPLATE_VARIABLES_EXPERIENCE } from '@confluence/experience-tracker';
import type { ComponentProps } from 'react';
import React, { PureComponent, Fragment } from 'react';
import type { WithApolloClient } from 'react-apollo';
import { Query, withApollo } from 'react-apollo';
import type { WrappedComponentProps } from 'react-intl-next';
import { injectIntl } from 'react-intl-next';
import idx from 'idx';
import type { ApolloError } from 'apollo-client';

import ButtonGroup from '@atlaskit/button/button-group';
import Button from '@atlaskit/button/custom-theme-button';
import { FormFooter } from '@atlaskit/form/FormFooter';
import { ConfluenceIcon } from '@atlaskit/logo';
import PageHeader from '@atlaskit/page-header';
import SectionMessage from '@atlaskit/section-message/section-message';
import { CONTEXT_PATH } from '@confluence-classic/confluence-urls';

import { ATLASSIAN_SUPPORT_URL, isUnauthorizedError } from '@confluence/error-boundary';
import {
	getExperienceTracker, // eslint-disable-line no-restricted-imports
	ExperienceStart,
} from '@confluence/experience-tracker';
import type { RoutesContextType } from '@confluence/route-manager';
import { addQueryParams, withRoutesContext } from '@confluence/route-manager';
import type { OnContentUpdatedResponse } from '@confluence/create-dialog-context';
import {
	convertGraphQLTemplateContentAppearanceToContentAppearanceType,
	CREATED_WITH_TEMPLATE_QUERY_PARAM_NAME,
	GraphQLTemplateContentAppearance,
	IN_EDITOR_TEMPLATES_QUERY_OPEN,
	IN_EDITOR_TEMPLATES_QUERY_PARAM_NAME,
} from '@confluence/template-utils';
import {
	ContentAppearanceType,
	getContentAppearanceForAtlaskit,
} from '@confluence/content-appearance';
import { EDIT_BLOG_V2, EDIT_PAGE_V2 } from '@confluence/named-routes';
import { processDraftUrl } from '@confluence/create-blank-fabric-page';

import Renderer from '../Renderer';
import { CustomFullPageDrawer } from './CustomFullPageDrawer';
import {
	submitVariablesCreateTemplateSuccess,
	submitVariablesCreateTemplateError,
} from './analytics';
import {
	VariableInputDrawerFooterWrapper,
	VariableInputDrawerHeader,
	VariableInputDrawerPageHeaderWrapper,
	VariableInputDrawerButtonWrapper,
} from './TemplateVariableInputStyles';
import { VariableInputDrawerExperimentalPublishPageByTemplate } from './VariableInputDrawerExperimentalPublishByTemplate.experimentalgraphql.js';
import { TemplateVariableInputDrawerQuery } from './VariableInputDrawerTemplateQuery.graphql.js';
import { VariableInputDrawerExperimentalUpdateExistingWithTemplate } from './VariableInputDrawerExperimentalUpdateExistingWithTemplate.experimentalgraphql.js';

type VariableInputDrawerWrapperProps = {
	onClose: () => void;
	spaceKey: string;
	parentPageId: string;
	templateId: string;
	title: string;
	isOpen: boolean;
	flags: any;
	createAnalyticsEvent: (event: any) => any;
	contentId?: string;
	onContentUpdated?: (response: OnContentUpdatedResponse) => void;
	onError?: (error: Error) => void;
	beforeUpdateRequest?: () => void;
	forceTemplatePanelOpen?: boolean;
	openContentInNewTab?: boolean;
};

type VariableInputDrawerProps = WithApolloClient<
	VariableInputDrawerWrapperProps & RoutesContextType & WrappedComponentProps
>;
type VariableInputDrawerState = {
	isLoading: boolean;
	isCreating: boolean;
};
type ContentProps = {
	Title: any;
	Body: any;
	isFullWidth: boolean;
};

type FooterProps = {
	isFullWidth: boolean;
	isLoading: boolean;
};

export class VariableInputDrawerClass extends PureComponent<
	VariableInputDrawerProps,
	VariableInputDrawerState
> {
	state = {
		isLoading: true,
		isCreating: false,
	};

	_onClose = () => {
		getExperienceTracker().abort({
			name: CREATE_PAGE_TEMPLATE_VARIABLES_EXPERIENCE,
			reason: 'closed dialog',
		});
		const { onClose } = this.props;
		onClose();
	};

	/**
	 * Called when creating draft with template content.
	 * @param context - template context containing variables and page context
	 * @return links object with draft link
	 * @throws error if the experimentalPublishPageByTemplate resolver throws an error
	 */
	_fetchDraftUrl = async (context): Promise<object> => {
		const { templateId, client, parentPageId, spaceKey, title, forceTemplatePanelOpen, match } =
			this.props;
		const blueprint = { templateId };
		const isEditorPage = match?.name === EDIT_PAGE_V2.name || match?.name === EDIT_BLOG_V2.name;

		// creating new instance from create dialog
		const draftObj = await client.mutate({
			mutation: VariableInputDrawerExperimentalPublishPageByTemplate,
			variables: {
				blueprint: JSON.stringify(blueprint),
				parentPageId: parentPageId || 'no-parent-page',
				context: JSON.stringify(context),
				spaceKey,
				title: title || '',
				attemptSpaUrlResponse: forceTemplatePanelOpen && !isEditorPage,
				forceTemplatePanelOpen,
			},
		});
		let draftUrl = idx(draftObj, (_) => _.data.experimentalPublishPageByTemplate.links.draftUrl);
		if (!draftUrl) {
			throw new Error('draftUrl not defined');
		}
		if (forceTemplatePanelOpen && !isEditorPage) {
			draftUrl = addQueryParams(draftUrl, {
				[IN_EDITOR_TEMPLATES_QUERY_PARAM_NAME]: IN_EDITOR_TEMPLATES_QUERY_OPEN,
				[CREATED_WITH_TEMPLATE_QUERY_PARAM_NAME]: 'true',
			});
		}

		return { draftUrl };
	};

	/**
	 * Called when updating a draft with existing contentId
	 * @param context - template context containing variables and page context
	 * @return OnContentUpdatedResponse containing content, links, metadata as a subset of update instance json response
	 * @private
	 */
	_fetchOnContentUpdatedResponse = async (context): Promise<OnContentUpdatedResponse> => {
		const { contentId, templateId, client, parentPageId, spaceKey, title } = this.props;
		const blueprint = { templateId };

		// updating an existing instance using content id
		const draftObj = await client.mutate({
			mutation: VariableInputDrawerExperimentalUpdateExistingWithTemplate,
			variables: {
				contentId,
				spaceKey,
				parentPageId,
				blueprint: JSON.stringify(blueprint),
				context: JSON.stringify(context),
				title: title || '',
			},
		});
		return idx(draftObj, (_) => _.data.experimentalUpdateDraftWithTemplate);
	};

	// https://github.com/final-form/react-final-form/issues/103
	// If users name variables using numbers it breaks the form
	// hacky fix added a $ character in front, this function is here to remove it
	_sanitizeData = (data) => {
		const sanitized = {};
		Object.keys(data).forEach((key) => {
			const cleanedKey = key.substr(1);
			sanitized[cleanedKey] = data[key];
		});
		return sanitized;
	};

	_onSubmit = async (data): Promise<boolean> => {
		const { beforeUpdateRequest } = this.props;
		const context = this._sanitizeData(data);
		this.setState({ isLoading: true, isCreating: true });
		try {
			if (this.props.contentId) {
				beforeUpdateRequest && beforeUpdateRequest();
				const onContentUpdatedResponse = await this._fetchOnContentUpdatedResponse(context);
				this._handleUpdateSubmitSuccess(onContentUpdatedResponse);
			} else {
				const draftUrlObj = await this._fetchDraftUrl(context);
				this._handleCreateSubmitSuccess(draftUrlObj);
			}
			this.setState({ isCreating: true });
			return true;
		} catch (errors) {
			this._handleSubmitError(errors);
			this.setState({ isLoading: false, isCreating: false });
			return false;
		}
	};

	_sendSuccessMetrics = () => {
		const { createAnalyticsEvent } = this.props;
		createAnalyticsEvent(submitVariablesCreateTemplateSuccess()).fire();
		getExperienceTracker().succeed({
			name: CREATE_PAGE_TEMPLATE_VARIABLES_EXPERIENCE,
		});
	};

	_handleCreateSubmitSuccess = (link) => {
		const { push, openContentInNewTab, onClose } = this.props;
		this._sendSuccessMetrics();
		const draftUrl = `${window.location.origin}${processDraftUrl(link.draftUrl)}`;

		if (openContentInNewTab) {
			window.open(draftUrl, '_blank', 'noreferrer');
			onClose();
		} else {
			push(draftUrl, true);
		}
	};

	/**
	 * Handle callback (editor refresh, state, etc.) after content is updated
	 * @param response - update instance response from experimental graphql call
	 * @private
	 */
	_handleUpdateSubmitSuccess = (response: OnContentUpdatedResponse) => {
		const { onContentUpdated, replace, onClose } = this.props;
		if (onContentUpdated) {
			onContentUpdated(response);
			onClose();
		} else {
			// if editui is null or undefined, the outside error boundary is hit (creation error)
			replace(`${window.location.origin}${CONTEXT_PATH}${response.content._links.editui}`);
		}
		this._sendSuccessMetrics();
	};

	_handleSubmitError = (error: ApolloError) => {
		const { flags, intl, createAnalyticsEvent, onError } = this.props;

		if (onError) {
			onError(error);
		} else {
			flags.showErrorFlag({
				title: intl.formatMessage({
					id: 'editor.template.variable.input.creation.error.title',
					defaultMessage: 'Something went wrong',
				}),
				description: intl.formatMessage({
					id: 'editor.template.variable.input.creation.error.description',
					defaultMessage:
						'Refresh the page and try again. If this keeps happening, let us know using the link below.',
				}),
				actions: [
					{
						content: intl.formatMessage({
							id: 'editor.template.variable.input.creation.error.action',
							defaultMessage: 'Visit Atlassian Support',
						}),
						onClick: () => {
							window.open(ATLASSIAN_SUPPORT_URL, '_blank');
						},
					},
				],
			});
		}

		if (isUnauthorizedError(error)) {
			this._sendSuccessMetrics();
		} else {
			createAnalyticsEvent(submitVariablesCreateTemplateError(`${error}`)).fire();
			getExperienceTracker().fail({
				name: CREATE_PAGE_TEMPLATE_VARIABLES_EXPERIENCE,
				error,
			});
		}
	};

	Icon = () => (
		<VariableInputDrawerButtonWrapper data-testid="variable-input-drawer-button-wrapper">
			<Button
				iconBefore={<ConfluenceIcon />}
				appearance="subtle"
				onClick={this._onClose}
				tabIndex={-1}
			/>
		</VariableInputDrawerButtonWrapper>
	);

	Footer = (props: FooterProps) => {
		const { intl } = this.props;

		return (
			<VariableInputDrawerFooterWrapper isFullWidth={props.isFullWidth}>
				<FormFooter>
					<ButtonGroup>
						<Button type="submit" appearance="primary" isLoading={props.isLoading}>
							{intl.formatMessage({
								id: 'create.name',
								defaultMessage: 'Create',
							})}
						</Button>
						<Button appearance="subtle" onClick={this._onClose}>
							{intl.formatMessage({
								id: 'button.cancel',
								defaultMessage: 'Cancel',
							})}
						</Button>
					</ButtonGroup>
				</FormFooter>
			</VariableInputDrawerFooterWrapper>
		);
	};

	Content = React.forwardRef(
		// @ts-ignore TODO: fix
		(props: ContentProps, ref: React.RefObject<HTMLDivElement>) => {
			return (
				<div ref={ref}>
					<VariableInputDrawerHeader isFullWidth={props.isFullWidth}>
						{props.Title}
						<SectionMessage>
							{
								// eslint-disable-next-line react/no-this-in-sfc
								this.props.intl.formatMessage({
									id: 'template.variables.fabric.section.message',
									defaultMessage:
										'This template uses variables. Once you fill them in, the information you supply will become part of the content in your new page.',
								})
							}
						</SectionMessage>
					</VariableInputDrawerHeader>
					{/* eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766 */}
					<div style={{ marginTop: token('space.250', '20px') }}>{props.Body}</div>
				</div>
			);
		},
	);

	/**
	 * When the render loads, it still takes time for content to show
	 * so there is a time period where it's just white
	 * using onComplete instead of graphql's loading makes the loading state more accurate
	 */
	onComplete = () => {
		if (!this.state.isCreating) {
			this.setState({ isLoading: false });
		}
	};

	getAdf(adfString) {
		try {
			return JSON.parse(adfString);
		} catch (error) {
			return {};
		}
	}

	_variableInputDrawer = ({ data, loading }) => {
		const { templateId, spaceKey, isOpen, title } = this.props;

		if (loading) {
			return null;
		}

		const adfString = idx(data, (_) => _.template.body.atlas_doc_format.value) || '{}';
		// title can be passed in if set through create template macro
		// with the following key values that are filled out and replaced by the BE @currentTime @spaceKey @spaceName
		const pageTitle = title || idx(data, (_) => _.template.name) || '';

		const contentAppearanceProperty =
			idx(data, (_) => _.template.templatePropertySet.contentAppearance) ||
			GraphQLTemplateContentAppearance.DEFAULT;

		const contentAppearance =
			convertGraphQLTemplateContentAppearanceToContentAppearanceType(contentAppearanceProperty);
		const contentAppearanceForRenderer = getContentAppearanceForAtlaskit(contentAppearance);

		const isFullWidth = contentAppearance === ContentAppearanceType.FULL_WIDTH;

		const TitleComponent = (
			<VariableInputDrawerPageHeaderWrapper>
				<PageHeader>{pageTitle}</PageHeader>
			</VariableInputDrawerPageHeaderWrapper>
		);

		return (
			<CustomFullPageDrawer
				isOpen={isOpen}
				Icon={this.Icon}
				hasForm
				Content={this.Content}
				Title={TitleComponent}
				Body={
					<Renderer
						adf={this.getAdf(adfString)}
						appearance={contentAppearanceForRenderer}
						objectId={templateId}
						spaceKey={spaceKey}
						showTemplateVariablesInPreview
						showTemplateInputInPreview
						onComplete={this.onComplete}
						allowPlaceholderText={true}
						isPreviewMode={true}
					/>
				}
				Footer={this.Footer}
				onSubmit={this._onSubmit}
				isLoading={this.state.isLoading}
				isFullWidth={isFullWidth}
			/>
		);
	};

	render() {
		const { templateId } = this.props;
		return (
			<Fragment>
				<ExperienceStart name={CREATE_PAGE_TEMPLATE_VARIABLES_EXPERIENCE} />
				<Query
					query={TemplateVariableInputDrawerQuery}
					variables={{ templateId }}
					fetchPolicy="network-only"
					errorPolicy="all"
				>
					{this._variableInputDrawer}
				</Query>
			</Fragment>
		);
	}
}

const injectedComponent = injectIntl(withRoutesContext(VariableInputDrawerClass));

export const VariableInputDrawerComponent =
	withApollo<ComponentProps<typeof injectedComponent>>(injectedComponent);
